// frontend/src/pages/ProductDetail.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import postService from "../services/postService";
import chatService from "../services/chatService";
import ReportButton from "../components/ReportButton";
import {
  MapPin,
  User,
  Calendar,
  Package,
  Heart,
  AlertCircle,
  Trash2,
  CheckCircle,
  ArrowLeft,
  Loader2,
  Share2,
  MessageCircle,
  Eye,
  Copy,
  Check,
  Flag,
  ZoomIn,
} from "lucide-react";
import Swal from "sweetalert2";

// Componente de placeholder
const ImagePlaceholder = () => (
  <div className="w-full aspect-[4/3] bg-gray-100 flex items-center justify-center rounded-xl">
    <Package className="w-16 h-16 text-gray-300" />
  </div>
);

// 🔥 COMPONENTE DE ZOOM - LUPA
const ZoomableImage = ({ src, alt, totalPhotos, currentIndex }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseMove = (e) => {
    if (!imageRef.current || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setMousePosition({
      x: Math.min(Math.max(x, 0), 100),
      y: Math.min(Math.max(y, 0), 100),
    });
  };

  const handleMouseEnter = () => {
    if (!isMobile) setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    if (!isMobile) setIsZoomed(false);
  };

  const handleClick = () => {
    if (isMobile) setIsZoomed(!isZoomed);
  };

  const handleCloseZoom = () => {
    setIsZoomed(false);
  };

  if (!src) return <ImagePlaceholder />;

  // 🔥 VISTA CON ZOOM EN DESKTOP (hover)
  if (!isMobile) {
    return (
      <div
        ref={containerRef}
        className="relative w-full aspect-[4/3] bg-white rounded-xl overflow-hidden border border-gray-100 cursor-zoom-in group"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <img
          ref={imageRef}
          src={src}
          alt="Produto"
          className="w-full h-full object-contain p-2 transition-transform duration-200"
        />

        {/* 🔥 CONTADOR DENTRO DE LA IMAGEN - ESQUINA INFERIOR IZQUIERDA */}
        {totalPhotos > 1 && (
          <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white text-[10px] px-2.5 py-0.5 rounded-full z-10 font-medium">
            {currentIndex + 1} / {totalPhotos}
          </div>
        )}

        <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <ZoomIn className="w-4 h-4" />
        </div>

        {isZoomed && (
          <div
            className="absolute pointer-events-none border-2 border-[#449879] rounded-full shadow-2xl"
            style={{
              width: "150px",
              height: "150px",
              left: `${mousePosition.x}%`,
              top: `${mousePosition.y}%`,
              transform: "translate(-50%, -50%)",
              backgroundImage: `url(${src})`,
              backgroundSize: "300% 300%",
              backgroundPosition: `${mousePosition.x}% ${mousePosition.y}%`,
              backgroundRepeat: "no-repeat",
              zIndex: 10,
            }}
          />
        )}
      </div>
    );
  }

  // 🔥 VISTA EN MOBILE (tap para ampliar)
  return (
    <>
      <div
        className="relative w-full aspect-[4/3] bg-white rounded-xl overflow-hidden border border-gray-100 cursor-pointer"
        onClick={handleClick}
      >
        <img
          src={src}
          alt="Produto"
          className="w-full h-full object-contain p-2"
        />

        {/* 🔥 CONTADOR DENTRO DE LA IMAGEN EN MOBILE */}
        {totalPhotos > 1 && (
          <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-white text-[10px] px-2.5 py-0.5 rounded-full z-10 font-medium">
            {currentIndex + 1} / {totalPhotos}
          </div>
        )}

        <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1">
          <ZoomIn className="w-3 h-3" />
          <span>Ampliar</span>
        </div>
      </div>

      {isZoomed && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 animate-fadeIn"
          onClick={handleCloseZoom}
        >
          <div className="relative w-full max-w-4xl max-h-[95vh]">
            <button
              onClick={handleCloseZoom}
              className="absolute -top-10 right-0 text-white/70 hover:text-white p-2 transition-colors z-10"
            >
              ✕ Fechar
            </button>

            <div className="w-full h-full flex items-center justify-center">
              <img
                src={src}
                alt="Produto"
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs">
              Toque fora da imagem para fechar
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// 🔥 GALERÍA DE FOTOS
const ImageGallery = ({ photos }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(0);

  const goToPrevious = () => {
    setSelectedPhoto((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setSelectedPhoto((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  if (!photos || photos.length === 0) {
    return <ImagePlaceholder />;
  }

  return (
    <div className="lg:sticky lg:top-24">
      <ZoomableImage
        src={photos[selectedPhoto]}
        alt="Produto"
        totalPhotos={photos.length}
        currentIndex={selectedPhoto}
      />

      {photos.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-[#449879] text-gray-700 hover:text-white rounded-full p-1.5 transition-all duration-200 shadow-md hover:scale-110 backdrop-blur-sm z-10"
            aria-label="Foto anterior"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-[#449879] text-gray-700 hover:text-white rounded-full p-1.5 transition-all duration-200 shadow-md hover:scale-110 backdrop-blur-sm z-10"
            aria-label="Próxima foto"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      )}

      {photos.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 justify-center mt-2">
          {photos.map((photo, index) => (
            <button
              key={index}
              onClick={() => setSelectedPhoto(index)}
              className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden border-2 transition-all ${
                index === selectedPhoto
                  ? "border-[#449879] ring-2 ring-[#449879]/20"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              aria-label={`Foto ${index + 1}`}
            >
              <img src={photo} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ==============================================
// 🔥 SELLER INFO - CON MENSAJE INICIAL SIEMPRE
// ==============================================
const SellerInfo = ({
  seller,
  isOwner,
  condominioNome,
  postId,
  postTitle,
  currentUser,
}) => {
  const navigate = useNavigate();
  const [chatLoading, setChatLoading] = useState(false);

  const handleChatClick = async () => {
    if (!currentUser) {
      Swal.fire({
        icon: "info",
        title: "Faça login",
        text: "Você precisa estar logado para conversar com o vendedor.",
        confirmButtonColor: "#449879",
        confirmButtonText: "Fazer login",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
          cancelButton: "rounded-xl px-6 py-2.5",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/login");
        }
      });
      return;
    }

    if (currentUser.status !== "aprovado") {
      Swal.fire({
        icon: "warning",
        title: "Conta em validação",
        text: "Aguarde a aprovação da sua conta para conversar com os vendedores.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
      return;
    }

    if (currentUser.id === seller?.id) {
      Swal.fire({
        icon: "info",
        title: "Seu anúncio",
        text: "Este é o seu próprio anúncio. Você não pode conversar consigo mesmo.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
      return;
    }

    setChatLoading(true);

    try {
      const response = await chatService.getOrCreateConversationWithMessage(
        postId,
        seller?.id
      );

      if (response.success) {
        // 🔥 MENSAJE INICIAL - SOLO SI ES NUEVA CONVERSACIÓN
        let url = `/chat/${response.data.id}`;

        if (response.is_new && response.initial_message) {
          const encodedMessage = encodeURIComponent(response.initial_message);
          url += `?initial_message=${encodedMessage}`;

          // Feedback visual
          Swal.fire({
            icon: "success",
            title: "💬 Conversa iniciada!",
            text: "Uma mensagem sugerida foi preparada para você.",
            timer: 2500,
            showConfirmButton: false,
            position: "top-end",
            toast: true,
            customClass: {
              popup: "rounded-2xl",
            },
          });
        } else {
          // Si ya existía conversación, mostrar mensaje diferente
          Swal.fire({
            icon: "info",
            title: "💬 Conversa existente",
            text: "Você já tem uma conversa com este vendedor.",
            timer: 2000,
            showConfirmButton: false,
            position: "top-end",
            toast: true,
            customClass: {
              popup: "rounded-2xl",
            },
          });
        }

        navigate(url);
      } else {
        throw new Error(response.message || "Erro ao iniciar conversa");
      }
    } catch (error) {
      console.error("Erro ao iniciar chat:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text:
          error.message ||
          "Não foi possível iniciar a conversa. Tente novamente.",
        confirmButtonColor: "#449879",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-6 py-2.5",
        },
      });
    } finally {
      setChatLoading(false);
    }
  };

  if (isOwner) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 text-center border border-blue-100/50">
        <p className="text-blue-700 font-semibold text-sm">
          Este é o seu anúncio
        </p>
        {condominioNome && condominioNome !== "Condomínio não informado" && (
          <p className="text-blue-500 text-xs mt-1 flex items-center justify-center gap-1">
            <MapPin className="w-3 h-3" />
            {condominioNome}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-50/50 rounded-xl p-4 space-y-3 border border-gray-100">
      <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-base">
        <User className="w-4 h-4 text-[#449879]" />
        Informações do Vendedor
      </h3>

      <div className="space-y-1.5">
        <p className="text-sm text-gray-700">
          <span className="font-medium">Vendedor:</span>{" "}
          {seller?.apelido || seller?.name || "Vendedor"}
        </p>

        {condominioNome && condominioNome !== "Condomínio não informado" && (
          <p className="text-sm text-gray-700 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-medium">Condomínio:</span>
            <span className="text-[#449879] font-medium">{condominioNome}</span>
          </p>
        )}
      </div>

      <div className="pt-2 border-t border-gray-200">
        <p className="text-[10px] text-gray-400 text-center mb-1.5">
          Converse diretamente com o vendedor sobre o produto
        </p>
        <button
          onClick={handleChatClick}
          disabled={chatLoading}
          className="w-full bg-[#449879] hover:bg-[#367d64] text-white py-2.5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {chatLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <MessageCircle className="w-4 h-4" />
          )}
          {chatLoading ? "Aguarde..." : "Eu quero"}
        </button>
      </div>
    </div>
  );
};

// Share Buttons
const ShareButtons = ({ title, url }) => {
  const [copied, setCopied] = useState(false);

  const encodedTitle = encodeURIComponent(`📦 ${title} - Brechó Vizinho`);
  const encodedUrl = encodeURIComponent(url);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      Swal.fire({
        icon: "success",
        title: "Link copiado!",
        text: "O link do produto foi copiado para a área de transferência.",
        timer: 1500,
        showConfirmButton: false,
        position: "top-end",
        toast: true,
        customClass: {
          popup: "rounded-2xl",
        },
      });
    } catch (err) {
      console.error("Erro ao copiar link:", err);
    }
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], "_blank", "width=600,height=400");
  };

  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-gray-800 flex items-center gap-2 text-base">
        <Share2 className="w-4 h-4 text-[#449879]" />
        Compartilhar
      </h3>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => handleShare("whatsapp")}
          className="bg-[#25D366] hover:bg-[#20b859] text-white py-2 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all transform hover:scale-105"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span>WhatsApp</span>
        </button>

        <button
          onClick={handleCopyLink}
          className="bg-[#449879] hover:bg-[#367d64] text-white py-2 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all transform hover:scale-105"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 flex-shrink-0" />
          ) : (
            <Copy className="w-3.5 h-3.5 flex-shrink-0" />
          )}
          <span>{copied ? "Copiado!" : "Copiar link"}</span>
        </button>
      </div>
    </div>
  );
};

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================
export default function ProductDetail() {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [identifier]);

  const loadProduct = async () => {
    setLoading(true);
    setError(null);

    const result = await postService.getPost(identifier);

    if (result.success) {
      setPost(result.data);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return "Grátis";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const formatDate = (date) => {
    if (!date) return "Data não informada";
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handleMarkAsSold = async () => {
    const result = await Swal.fire({
      title: "Marcar como vendido?",
      text: "Este produto será marcado como vendido e não aparecerá mais na home.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#449879",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, marcar",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "rounded-2xl",
        confirmButton: "rounded-xl px-6 py-2.5",
        cancelButton: "rounded-xl px-6 py-2.5",
      },
    });

    if (result.isConfirmed) {
      setActionLoading(true);
      const response = await postService.markAsSold(post.id);
      setActionLoading(false);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Produto marcado como vendido!",
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            popup: "rounded-2xl",
          },
        });
        loadProduct();
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: response.error,
          customClass: {
            popup: "rounded-2xl",
            confirmButton: "rounded-xl px-6 py-2.5",
          },
        });
      }
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "Excluir anúncio?",
      text: "Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "rounded-2xl",
        confirmButton: "rounded-xl px-6 py-2.5",
        cancelButton: "rounded-xl px-6 py-2.5",
      },
    });

    if (result.isConfirmed) {
      setActionLoading(true);
      const response = await postService.deletePost(post.id);
      setActionLoading(false);

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Anúncio excluído!",
          timer: 1500,
          showConfirmButton: false,
          customClass: {
            popup: "rounded-2xl",
          },
        });
        navigate("/");
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: response.error,
          customClass: {
            popup: "rounded-2xl",
            confirmButton: "rounded-xl px-6 py-2.5",
          },
        });
      }
    }
  };

  const isOwner = user && post && user.id === post.user_id;
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 text-[#449879] animate-spin" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Produto não encontrado
          </h2>
          <p className="text-gray-500 mb-4">
            {error ||
              "O produto que você está procurando não existe ou foi removido."}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#449879] text-white px-6 py-3 rounded-xl hover:bg-[#367d64] transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para a home
          </Link>
        </div>
      </div>
    );
  }

  const condominioNome = post.condominio?.nome || "Condomínio não informado";

  return (
    <div className="min-h-screen bg-gray-50 pt-2 pb-24 md:py-4 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#449879] mb-3 md:mb-4 transition-colors group text-sm"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Voltar</span>
        </button>

        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-8">
          <div className="order-1 lg:order-none">
            <ImageGallery photos={post.fotos} />
          </div>

          <div className="order-2 lg:order-none space-y-4">
            <div className="flex flex-wrap gap-1.5">
              {post.tipo === "Doação" && (
                <span className="bg-green-50 text-green-700 text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-medium">
                  <Heart className="w-3 h-3" />
                  Doação
                </span>
              )}
              {post.condicao && (
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    post.condicao === "novo"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-gray-50 text-gray-700"
                  }`}
                >
                  {post.condicao === "novo" ? "🆕 Novo" : "🔄 Usado"}
                </span>
              )}
              {post.is_sold && (
                <span className="bg-red-50 text-red-700 text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-medium">
                  <CheckCircle className="w-3 h-3" />
                  Vendido
                </span>
              )}
              {post.status === "pending" && (
                <span className="bg-yellow-50 text-yellow-700 text-xs px-2.5 py-1 rounded-full font-medium">
                  Pendente
                </span>
              )}
            </div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
              {post.titulo}
            </h1>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#449879]">
                {post.tipo === "Venda" ? formatPrice(post.preco) : "Grátis"}
              </p>
              {post.tipo === "Venda" && post.preco > 0 && (
                <span className="text-gray-400 text-xs">à vista</span>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-2 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm text-gray-600 py-1.5 border-b border-gray-50">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#449879] flex-shrink-0" />
                  <span className="font-medium w-24">Categoria:</span>
                </div>
                <span>{post.categoria || "Sem categoria"}</span>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm text-gray-600 py-1.5 border-b border-gray-50">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#449879] flex-shrink-0" />
                  <span className="font-medium w-24">Localização:</span>
                </div>
                <span className="text-[#449879] font-medium break-words">
                  {condominioNome}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm text-gray-600 py-1.5 border-b border-gray-50">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="font-medium w-24">Publicado em:</span>
                </div>
                <span>{formatDate(post.created_at)}</span>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm text-gray-600 py-1.5">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="font-medium w-24">Status:</span>
                </div>
                <span
                  className={
                    post.is_sold
                      ? "text-red-600 font-medium"
                      : "text-green-600 font-medium"
                  }
                >
                  {post.is_sold ? "Vendido" : "Disponível"}
                </span>
              </div>
            </div>
            {post.descricao && (
              <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
                <h3 className="font-semibold text-gray-800 mb-2 text-base">
                  Descrição
                </h3>
                <p className="text-gray-600 whitespace-pre-wrap leading-relaxed text-sm">
                  {post.descricao}
                </p>
              </div>
            )}
            <SellerInfo
              seller={post.user}
              isOwner={isOwner}
              condominioNome={condominioNome}
              postId={post.id}
              postTitle={post.titulo}
              currentUser={user}
            />
            {isOwner && !post.is_sold && (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleMarkAsSold}
                  disabled={actionLoading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 transform hover:scale-[1.02] text-sm"
                >
                  <CheckCircle className="w-4 h-4" />
                  Marcar como vendido
                </button>

                <button
                  onClick={() => navigate(`/perfil?tab=posts&edit=${post.id}`)}
                  disabled={actionLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 transform hover:scale-[1.02] text-sm"
                >
                  Editar anúncio
                </button>

                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 transform hover:scale-[1.02] text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
            <ShareButtons title={post.titulo} url={currentUrl} />
            {!isOwner && (
              <div className="flex justify-start pt-1 border-t border-gray-100">
                <ReportButton
                  postId={post.id}
                  postTitle={post.titulo}
                  currentUser={user}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.25s ease-out;
          }
          .cursor-zoom-in {
            cursor: zoom-in;
          }
        `}
      </style>
    </div>
  );
}
